import { useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, horodater } from "@/lib/api";

const fetchRapport = (format, periode) => api.get("/rapprochement/rapport", { params: { format, ...periode }, responseType: "blob" });
export const nomRapport = (p) => `Rapprochement du ${p.debut} au ${p.fin}`;

export const telecharger = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = horodater(name);
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// Rapports/<année> dans le dossier choisi (année du début de la période)
const dossierRapports = async (dirHandle, annee) =>
  (await dirHandle.getDirectoryHandle("Rapports", { create: true })).getDirectoryHandle(String(annee), { create: true });

// Clé période + format d'un rapport « Rapprochement du … », sinon null
const cleRapport = (nom) => {
  const m = /^Rapprochement du (\d{4}-\d{2}-\d{2}) au (\d{4}-\d{2}-\d{2}).*\.(pdf|csv)$/i.exec(nom);
  return m ? `${m[1]}|${m[2]}|${m[3].toLowerCase()}` : null;
};

// Date d'origine la plus récente de chaque période + format déjà présente dans un dossier
async function datesExistantes(dossier) {
  const dates = {};
  for await (const x of dossier.values()) {
    const cle = x.kind === "file" && cleRapport(x.name);
    if (cle) dates[cle] = Math.max(dates[cle] || 0, (await x.getFile()).lastModified);
  }
  return dates;
}

// Déplace dans Rapports/<année> les anciens rapports « Rapprochement du … » restés à la racine ; renvoie le nombre déplacé.
// Les gagnants sont choisis AVANT toute copie (une copie prend la date du jour) : pour chaque période + format, seul le plus
// récent de la racine entre dans Rapports/<année>, et seulement s'il bat les versions déjà présentes ; les autres vont
// directement dans « Anciennes versions ».
export async function rangerAnciensRapports(dirHandle) {
  const anciens = [];
  for await (const e of dirHandle.values()) {
    const m = e.kind === "file" && /^Rapprochement du (\d{4})-.*\.(pdf|csv)$/i.exec(e.name);
    if (m) anciens.push({ e, annee: m[1], cle: cleRapport(e.name), fichier: await e.getFile() });
  }
  if (!anciens.length) return 0;
  const dossiers = {}, existants = {}, meilleur = {};
  for (const a of anciens) {
    if (!dossiers[a.annee]) {
      dossiers[a.annee] = await dossierRapports(dirHandle, a.annee);
      existants[a.annee] = await datesExistantes(dossiers[a.annee]);
    }
    if (!a.cle) continue;
    const k = `${a.annee}|${a.cle}`;
    if (!meilleur[k] || a.fichier.lastModified > meilleur[k].fichier.lastModified) meilleur[k] = a;
  }
  let n = 0;
  for (const a of anciens) {
    const dossier = dossiers[a.annee];
    const garde = !a.cle || (meilleur[`${a.annee}|${a.cle}`] === a && a.fichier.lastModified >= (existants[a.annee][a.cle] || 0));
    const cible = garde ? dossier : await dossier.getDirectoryHandle("Anciennes versions", { create: true });
    const w = await (await cible.getFileHandle(a.e.name, { create: true })).createWritable();
    await w.write(a.fichier); await w.close();
    await dirHandle.removeEntry(a.e.name);
    n += 1;
  }
  return n;
}

// Dans chaque Rapports/<année> : pour une même période et un même format, garde le fichier le plus récent
// et déplace les autres dans Rapports/<année>/Anciennes versions ; renvoie le nombre déplacé
export async function nettoyerDoublonsRapports(dirHandle) {
  let racine;
  try { racine = await dirHandle.getDirectoryHandle("Rapports"); } catch { return 0; }
  let n = 0;
  for await (const annee of racine.values()) {
    if (annee.kind !== "directory") continue;
    const groupes = {};
    for await (const e of annee.values()) {
      const cle = e.kind === "file" && cleRapport(e.name);
      if (cle) (groupes[cle] ||= []).push({ e, date: (await e.getFile()).lastModified });
    }
    for (const liste of Object.values(groupes)) {
      if (liste.length < 2) continue;
      liste.sort((a, b) => b.date - a.date);
      const archives = await annee.getDirectoryHandle("Anciennes versions", { create: true });
      for (const { e } of liste.slice(1)) {
        const w = await (await archives.getFileHandle(e.name, { create: true })).createWritable();
        await w.write(await e.getFile()); await w.close();
        await annee.removeEntry(e.name);
        n += 1;
      }
    }
  }
  return n;
}

// Écrit le PDF et le CSV dans Rapports/<année> du dossier (Chrome/Edge) sinon les télécharge ; renvoie true si écrits dans le dossier
export async function saveRapports(periode, dirHandle) {
  const nom = nomRapport(periode);
  const [pdf, csv] = await Promise.all([fetchRapport("pdf", periode), fetchRapport("csv", periode)]);
  if (dirHandle) {
    try {
      const cible = await dossierRapports(dirHandle, periode.debut.slice(0, 4));
      for (const [ext, r] of [["pdf", pdf], ["csv", csv]]) {
        const w = await (await cible.getFileHandle(horodater(`${nom}.${ext}`), { create: true })).createWritable();
        await w.write(r.data); await w.close();
      }
      return true;
    } catch { /* écriture refusée : on télécharge */ }
  }
  telecharger(pdf.data, `${nom}.pdf`);
  telecharger(csv.data, `${nom}.csv`);
  return false;
}

const CHOIX = [
  { v: "taxes", l: "TPS & TVQ seulement" },
  { v: "commissions", l: "Commissions seulement" },
  { v: "tout", l: "TPS & TVQ et Commissions" },
];

export const RapportButtons = ({ periode }) => {
  const [busy, setBusy] = useState("");
  const [reclam, setReclam] = useState("tout");
  const dl = async (format) => {
    setBusy(format);
    try {
      const r = await fetchRapport(format, { ...(periode || {}), reclamations: reclam });
      const m = /filename\*=UTF-8''([^;]+)/.exec(r.headers["content-disposition"] || "");
      telecharger(r.data, m ? decodeURIComponent(m[1]) : `rapprochement.${format}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1" title="Réclamations et lettres incluses dans le rapport" data-testid="rapport-reclamations-choix">
        <span className="text-[11px] text-slate-500 mr-1">Réclamations (avec lettres) :</span>
        {CHOIX.map((c) => (
          <label key={c.v} className="inline-flex items-center gap-1 text-xs cursor-pointer px-1">
            <input type="radio" name="rapport-reclamations" checked={reclam === c.v} onChange={() => setReclam(c.v)} className="accent-emerald-700" data-testid={`rapport-reclamations-${c.v}`} />
            {c.l}
          </label>
        ))}
      </div>
      {["pdf", "csv"].map((f) => (
        <Button key={f} variant="outline" className="bg-white" disabled={!!busy} onClick={() => dl(f)} data-testid={`rapport-${f}-button`}>
          {busy === f ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Rapport {f.toUpperCase()}
        </Button>
      ))}
    </>
  );
};
